import React, { useEffect } from "react"
import PropTypes from "prop-types"
import mount from "core/mount"
import { Note } from "@kit/design/note"
import { LinkButton } from "@kit/design/link-button"
import { Telemetry } from "@kit/design/event-emitter"
import useLocalStorage from "@kit/design/use-localstorage"
import { SignalsLaunchOfferNote } from "app/signals-launch-offer/signals-launch-offer-note"
import { usePromoCode } from "hooks/use-promo-code"
import { useCurrentUser } from "hooks/use-current-user"
import { analytics } from "utils/analytics"

export const DISMISSED_STORAGE_KEY = "signals-launch-offer-banner:dismissed"
export const BANNER_SOURCE = "signals_discount_banner"

export function SignalsLaunchOfferBanner({
  percentOff,
  targetInterval,
  endsAt
}) {
  const [dismissed, setDismissed] = useLocalStorage(
    DISMISSED_STORAGE_KEY,
    false
  )
  const { isLaunchOfferApplied } = usePromoCode()
  const { signalsLaunchOffer } = useCurrentUser()
  const visible = !dismissed && isLaunchOfferApplied

  // Which offer the creator saw, so banner performance can be sliced by coupon.
  const { code, segment, tier } = signalsLaunchOffer ?? {}
  const offerProperties = {
    promo_code: code,
    percent_off: percentOff,
    target_interval: targetInterval,
    segment,
    tier
  }

  useEffect(() => {
    if (!visible) return

    // Buttons and modals track themselves, but banner views have to be sent.
    analytics.track("Element", "Opened", {
      element_type: "banner",
      name: BANNER_SOURCE,
      ...offerProperties
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  if (!visible) return null

  // The CTA and dismiss buttons send their clicks automatically with these.
  return (
    <Telemetry value={{ location: BANNER_SOURCE, ...offerProperties }}>
      <div className="p-4">
        <SignalsLaunchOfferNote offer={{ percentOff, targetInterval, endsAt }}>
          <Note.Actions>
            <LinkButton
              variant="secondary"
              size="small"
              href={`/signals?source=${BANNER_SOURCE}`}
              telemetry={{ name: `${BANNER_SOURCE}_claim` }}
            >
              Claim your offer
            </LinkButton>
          </Note.Actions>
          <Note.Dismiss onDismiss={() => setDismissed(true)} />
        </SignalsLaunchOfferNote>
      </div>
    </Telemetry>
  )
}

SignalsLaunchOfferBanner.propTypes = {
  percentOff: PropTypes.number.isRequired,
  targetInterval: PropTypes.oneOf(["year", "month"]).isRequired,
  endsAt: PropTypes.string
}

mount({ "Layout.SignalsLaunchOfferBanner": SignalsLaunchOfferBanner })
